import type { ChangeEvent } from 'react'
import { CheckCircle2, XCircle } from 'lucide-react'
import type { ProPromoCodeValidation } from '@/shared/domain'
import { promoAppliedMessage } from '@/modules/auth/ui/components'
import { Button, Input, Label } from '@/shared/ui/primitives'

export type ProPromoFieldProps = {
  value: string
  applied: boolean
  appliedPromo: ProPromoCodeValidation | null
  isApplying: boolean
  disabled: boolean
  error: string | null
  onChange: (value: string) => void
  onApply: () => void
}

function inputStateClass(applied: boolean, error: string | null): string {
  if (applied) return 'border-sage/40'
  if (error) return 'border-destructive/40'
  return ''
}

export function ProPromoField({
  value,
  applied,
  appliedPromo,
  isApplying,
  disabled,
  error,
  onChange,
  onApply,
}: ProPromoFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor="pro-promo-code">Promo code</Label>
      <div className="flex items-start gap-2">
        <Input
          id="pro-promo-code"
          data-testid="pro-promo-code"
          value={value}
          disabled={disabled}
          aria-invalid={error !== null}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="Optional"
          className={`bg-card ${inputStateClass(applied, error)}`}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        />
        <Button
          type="button"
          variant="outline"
          data-testid="pro-apply-promo"
          onClick={onApply}
          disabled={disabled || isApplying || value === '' || applied}
          className={applied ? 'border-sage/40 text-sage' : undefined}
        >
          {isApplying ? 'Applying…' : applied ? 'Applied' : 'Apply'}
        </Button>
      </div>
      {applied && appliedPromo ? (
        <p
          className="flex items-center gap-1.5 rounded-md bg-sage-soft px-2.5 py-1.5 text-xs font-medium text-sage"
          data-testid="pro-promo-applied"
        >
          <CheckCircle2 aria-hidden className="size-3.5 shrink-0" />
          {promoAppliedMessage(appliedPromo)}
        </p>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="flex items-center gap-1.5 rounded-md bg-destructive/10 px-2.5 py-1.5 text-xs font-medium text-destructive"
        >
          <XCircle aria-hidden className="size-3.5 shrink-0" />
          {error}
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground">Only applies to Stripe checkout.</p>
    </div>
  )
}
